import { useState, useMemo } from 'react';
import { Review, reviews, categories } from '@/mocks/reviews';
import VideoCard from '@/components/base/VideoCard';
import FilterBar from './FilterBar';
import VideoPopup from '@/components/feature/VideoPopup';

export default function ReviewGrid() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [popupReview, setPopupReview] = useState<Review | null>(null);

  const filteredReviews = useMemo(() => {
    let result = reviews;

    if (selectedCategory !== 'all') {
      result = result.filter((r) => r.category === selectedCategory);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (r) =>
          r.customerName.toLowerCase().includes(q) ||
          r.procedure.toLowerCase().includes(q) ||
          r.location.toLowerCase().includes(q)
      );
    }

    return result;
  }, [selectedCategory, searchQuery]);

  const handlePopupNavigate = (review: Review) => {
    setPopupReview(review);
  };

  return (
    <section id="reviews" className="w-full scroll-mt-20">
      <FilterBar
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        categories={categories}
      />

      <div className="w-full px-4 md:px-6 pb-12 md:pb-16">
        <div className="max-w-6xl mx-auto">
          {filteredReviews.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-search-line text-foreground-300 text-2xl"></i>
              </span>
              <p className="text-sm text-foreground-500">Aramanızla eşleşen yorum bulunamadı.</p>
              <button
                onClick={() => {
                  setSelectedCategory('all');
                  setSearchQuery('');
                }}
                className="px-4 py-2 rounded-full bg-primary-50 text-primary-600 text-sm font-medium hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Filtreleri Temizle
              </button>
            </div>
          ) : (
            <div
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5"
              data-product-shop
            >
              {filteredReviews.map((review) => (
                <VideoCard
                  key={review.id}
                  review={review}
                  onVideoClick={setPopupReview}
                />
              ))}
            </div>
          )}

          {filteredReviews.length > 0 && (
            <div className="flex flex-col items-center gap-2 mt-10 md:mt-12">
              <p className="text-sm text-foreground-400">
                Toplam <strong className="text-foreground-700">{filteredReviews.length}</strong> yorum görüntüleniyor
              </p>
              <a
                href="https://wa.me/905433724578?text=Merhaba%2C%20daha%20fazla%20yorum%20g%C3%B6rmek%20istiyorum."
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-whatsapp-fill"></i>
                </span>
                Daha Fazla Yorum İçin Bize Ulaşın
              </a>
            </div>
          )}
        </div>
      </div>

      {popupReview && (
        <VideoPopup
          review={popupReview}
          reviews={filteredReviews.length > 0 ? filteredReviews : reviews}
          onClose={() => setPopupReview(null)}
          onNavigate={handlePopupNavigate}
        />
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </section>
  );
}